import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useNavigate, useParams } from 'react-router'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  BOOKING_SESSION_STATUS_MAP,
  EVENT_TYPES_ARRAY,
} from 'src/consumer/constants'
import { getVcAuditViewerBootstrap } from './vcAuditSessionsService'
import SupportAuditRtcVideoSurface from './SupportAuditRtcVideoSurface'

function formatDateTime(value) {
  if (!value) {
    return 'NA'
  }

  return dayjs(value).format('DD MMM YYYY, hh:mm A')
}

function getEventTypeLabel(eventType) {
  return (
    EVENT_TYPES_ARRAY.find((option) => option?.id === eventType)?.title ||
    eventType ||
    'Unknown Event'
  )
}

function getSessionStatusLabel(sessionStatus) {
  return (
    BOOKING_SESSION_STATUS_MAP?.[sessionStatus]?.title ||
    sessionStatus ||
    'Unknown'
  )
}

export default function VcAuditSessionLiveViewer() {
  const navigate = useNavigate()
  const { bookedEventId } = useParams()
  const [viewerBootstrap, setViewerBootstrap] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true

    async function loadViewerBootstrap({ silent = false } = {}) {
      if (!silent) {
        setLoading(true)
        setError('')
      }

      const result = await getVcAuditViewerBootstrap(bookedEventId)

      if (!active) {
        return
      }

      if (!result.ok) {
        // On a background poll, keep the last good state rather than flashing an error.
        if (!silent) {
          setViewerBootstrap(null)
          setError(result.message || 'Unable to load the live viewer.')
          setLoading(false)
        }
        return
      }

      setViewerBootstrap(result.bootstrap || null)
      setError('')
      setLoading(false)
    }

    loadViewerBootstrap()

    // Poll so the viewer flips to the live stream the moment the session goes live (and back
    // out when it ends) without the auditor having to reload. Stops on unmount.
    const pollInterval = setInterval(() => {
      loadViewerBootstrap({ silent: true })
    }, 15000)

    return () => {
      active = false
      clearInterval(pollInterval)
    }
  }, [bookedEventId])

  const session = viewerBootstrap?.session || null
  const bookedEvent = session?.bookedEvent || viewerBootstrap?.bookedEvent || {}
  const clinic = session?.clinic || {}
  const mentor = session?.mentor || {}
  const patient = session?.patient || {}
  const canViewLive = session?.canViewLive !== false
  const isLive = session?.isLive === true
  const viewerMessage = useMemo(() => {
    if (error) {
      return error
    }

    if (!loading && !canViewLive) {
      return 'Your VC audit profile does not allow live access to this session.'
    }

    if (!loading && session && !isLive) {
      return 'This session is not live right now — the live viewer opens automatically the moment it goes on air. Keep this page open; it refreshes on its own.'
    }

    return ''
  }, [canViewLive, error, isLive, loading, session])

  return (
    <>
      <SupportHeader
        heading="Live Session Viewer"
        subHeading={bookedEvent?.eventName || getEventTypeLabel(bookedEvent?.eventType)}
        showBackButton
        backPath="/support-home/booked-events"
      />

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          minHeight: 'calc(100vh - 120px)',
        }}
      >
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', lg: 'center' }}
            >
              <Box>
                <Typography variant="h6">
                  {bookedEvent?.eventName || getEventTypeLabel(bookedEvent?.eventType)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(bookedEvent?.startTime)}
                  {bookedEvent?.endTime
                    ? ` to ${formatDateTime(bookedEvent?.endTime)}`
                    : ''}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={getEventTypeLabel(bookedEvent?.eventType)} />
                <Chip
                  label={getSessionStatusLabel(bookedEvent?.sessionStatus)}
                  variant="outlined"
                />
                {isLive ? (
                  <Chip
                    color="error"
                    icon={<FiberManualRecordIcon />}
                    label={session?.liveBadgeLabel || 'Live'}
                  />
                ) : null}
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Clinic
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {clinic?.clinicBrandName || 'Unknown Clinic'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clinic?.clinicLocationName || 'Unknown Location'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Practitioner
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {mentor?.name || 'Unknown Practitioner'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Patient
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {patient?.name || patient?.patientNumber || 'Unknown Patient'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            flex: 1,
            minHeight: { xs: '62vh', lg: '70vh' },
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: '100%', minHeight: { xs: '62vh', lg: '70vh' } }}>
            {loading ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : viewerMessage ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: 3,
                }}
              >
                <Alert severity={error ? 'error' : 'info'} sx={{ width: '100%', maxWidth: 720 }}>
                  {viewerMessage}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/support-home/booked-events')}
                >
                  Back to booked events
                </Button>
              </Box>
            ) : (
              <SupportAuditRtcVideoSurface
                viewerBootstrap={viewerBootstrap}
                handleLeaveRoom={() => navigate('/support-home/booked-events')}
              />
            )}
          </Box>
        </Card>
      </Box>
    </>
  )
}
